import { useAdsContext } from "@/ads/context";
import { useGDPR } from "@/contexts/GDPR";
import { useCallback, useEffect, useRef } from "react";
/**  initialize google ads and define slots
 * 1. wait for GPT to be ready
 * 2. setup GPT
 * 3. define slots
 * 4. set targeting
 * 5. enable services
 * @returns void
 *
 */
export const useGoogleAdsInit = () => {
    const { adSlots, targeting, registerSlot, setGptInitialized, setLoadedSlot, resetSlots, hasDeviceTypeChanged, userDevice, loadedSlots, deleteAllSlots, } = useAdsContext();
    const { isGDPRReady, consentHasChanged } = useGDPR();
    const isInitialized = useRef(false);
    const deviceTypeChanged = useRef(undefined); // to check if device type has changed only once
    const retry = useRef(0); // retry counter for checking if GPT is ready
    const limit = useRef(10); // retry limit for checking if GPT is ready
    /**
     * set targeting for the ads
     * if slot is provided, set targeting on slot level
     * else set targeting on pubads level
     * @param targeting
     * @param slot
     */
    const setTargeting = (targeting, slot) => {
        Object.entries(targeting).forEach(([key, value]) => {
            // targeting can be done on slot level or on pubads level
            if (slot) {
                slot.setTargeting(key, Array.isArray(value) ? value : String(value));
            }
            else {
                googletag
                    .pubads()
                    .setTargeting(key, Array.isArray(value) ? value : String(value));
            }
        });
    };
    const defineSlots = useCallback(() => {
        // Define the slots using googletag
        adSlots.forEach((ad) => {
            var _a;
            const slot = (_a = googletag
                .defineSlot(ad.adUnit, ad.size, ad.id)) === null || _a === void 0 ? void 0 : _a.addService(googletag.pubads());
            // Add slot-level targeting if available
            if (ad.specificTargeting) {
                setTargeting(ad.specificTargeting, slot);
            }
            // Keep track of this slot
            if (slot) {
                registerSlot(slot.getSlotElementId(), slot);
            }
        });
        const slotStatus = {};
        // Listen for ad render events to track loaded slots
        googletag.pubads().addEventListener("slotRenderEnded", (event) => {
            const slotId = event.slot.getSlotElementId();
            // Avoid overwriting if already marked as loaded
            // because yieldLove also fires slotRenderEnded event
            // and we don't want to overwrite the status and avoid conflict
            // If the slot is already loaded, exit early
            if (slotStatus[slotId] === "loaded")
                return;
            // Update the status of the slot
            // If the slot is empty, mark it as "failed"
            // Otherwise, mark it as "loaded"
            const status = event.isEmpty ? "failed" : "loaded";
            slotStatus[slotId] = status;
            setLoadedSlot(slotId, status);
        });
    }, [adSlots, registerSlot, setLoadedSlot]);
    const displayAndRefreshSlots = useCallback(() => {
        // Display and refresh all loaded slots
        Object.keys(loadedSlots).forEach((slotId) => {
            window.googletag.display(slotId);
        });
        window.googletag.pubads().refresh();
    }, [loadedSlots]);
    // Setup and initialize Google Publisher Tags (GPT)
    const initializeGPT = useCallback(() => {
        googletag.cmd.push(() => {
            // Set global targeting parameters
            setTargeting(targeting);
            defineSlots();
            // Configure GPT services
            // Enable single request mode for better performance
            // This allows multiple ad slots to be requested in a single call
            // instead of separate calls for each slot
            googletag.pubads().enableSingleRequest();
            // Enable lazy loading for ads
            // This will load ads only when they are needed
            // and render them when they are within 2 viewports
            // This helps improve page load performance
            googletag.pubads().enableLazyLoad({
                fetchMarginPercent: 400, // Start loading 4 viewports before
                renderMarginPercent: 200, // Render 2 viewports before
                mobileScaling: 2.0, // Double the margin on mobile
            });
            // Track slot render results
            // Enable the GPT services
            googletag.enableServices();
        });
    }, [targeting, defineSlots]);
    useEffect(() => {
        if (hasDeviceTypeChanged && deviceTypeChanged.current !== userDevice) {
            // delete the loaded slots state when device type changes
            deleteAllSlots();
            if (Object.keys(loadedSlots).length !== 0)
                return;
            // Redefine the slots, display them, and refresh
            googletag.cmd.push(() => {
                googletag.destroySlots();
                defineSlots();
                displayAndRefreshSlots();
            });
            deviceTypeChanged.current = userDevice;
        }
    }, [
        hasDeviceTypeChanged,
        userDevice,
        resetSlots,
        defineSlots,
        displayAndRefreshSlots,
        loadedSlots,
        deleteAllSlots,
    ]);
    // Check if GPT is initialized and GDPR is ready
    useEffect(() => {
        if (isInitialized.current || !isGDPRReady)
            return;
        // Wait for GPT to be ready before initializing
        const waitForGPT = (onReady) => {
            const tryInitialize = () => {
                var _a, _b;
                // Check if GPT is available and properly loaded
                const isGPTAvailable = ((_a = window === null || window === void 0 ? void 0 : window.googletag) === null || _a === void 0 ? void 0 : _a.apiReady) &&
                    typeof (googletag === null || googletag === void 0 ? void 0 : googletag.pubads) === "function" &&
                    typeof ((_b = googletag === null || googletag === void 0 ? void 0 : googletag.pubads()) === null || _b === void 0 ? void 0 : _b.setTargeting) === "function";
                // If already initialized, exit early
                if (isInitialized.current || !isGDPRReady)
                    return;
                // If GPT is available, initialize it
                // and set the targeting
                if (isGPTAvailable) {
                    isInitialized.current = true;
                    setGptInitialized(true);
                    onReady();
                    // retry initialization after a short delay
                }
                else if (retry.current < limit.current) {
                    retry.current++;
                    setTimeout(tryInitialize, 5);
                }
            };
            tryInitialize();
        };
        waitForGPT(initializeGPT);
    }, [
        adSlots,
        targeting,
        registerSlot,
        setGptInitialized,
        setLoadedSlot,
        isGDPRReady,
        consentHasChanged,
        resetSlots,
        initializeGPT,
    ]);
};
